/**
 * Ce que la démo raconte — les conversations, et ce qui s'y est passé.
 *
 * Kayla, 07-10-2026, en préparant la vidéo qui FORME les chatteurs : « que ce
 * soit plus réaliste ». Le poste qu'ils vont tenir n'a plus de Cyrano — « on
 * l'utilise pas, c'est du legacy » — et c'est donc ce poste-là que la démo
 * montre :
 *
 *   · le shift démarre et **Ma file est vide** : les fans attendent dans
 *     « Discussions », et c'est le chatteur qui les ajoute (cf. `monde.ts`) ;
 *   · un fan NOUVEAU dit « salut » ; le chatteur lance le flow « script 1 »,
 *     le flow écrit pour lui (`flows.ts`), et quand il se termine sur « tu vas
 *     devoir me payer », le fan demande combien — c'est là que le chatteur
 *     vend : une vidéo faite pour lui, 50 €, le lien, il paie ;
 *   · Francis, à droite, dit à chaque fois où on en est et quoi écrire
 *     (`conseil.ts`) ;
 *   · un fan étranger arrive traduit (`langues.ts`) ;
 *   · et pendant tout le shift, d'autres fans arrivent ou reviennent
 *     (`scenario.ts`, la vie de fond).
 *
 * 🪦 Ce fichier portait l'ultimatum de Cyrano, celui du jeu, le cadeau et la
 * demande de partie — les trois mécaniques de Cyrano 2.0 — et le `/menu`,
 * retiré du produit le 14-09. Plus rien de tout ça n'est le métier du
 * chatteur : une démo qui le montre forme à un poste qui n'existe plus.
 *
 * La méthode de vente vient de la mémoire de Kayla (14-09-2026) : ELLE
 * propose, jamais « ton budget ? » ; échelle 50 · 70 · 100 · 150 · 200, la
 * première vidéo à 50 et jamais moins ; la vidéo se vend comme faite pour lui
 * — durée, ce qu'on y voit, « rien que pour toi » ; un prix ne redescend pas
 * (50 → 40 au plus) ; le réel (appel, cam, rencontre, snap) se détourne, il
 * ne se vend pas.
 */
import type { ApiConversationListItem } from "@/panels/conversations/atoms";
import type { Stage } from "@/panels/conversations/mockData";

import { FANS, traduireVers, type Dit, type Langue } from "./langues";
import {
  bulleDuFan,
  idDemo,
  ilYA,
  message,
  noteVide,
  profilFan,
  tagLite,
  type ConversationDemo,
  type CreatriceDemo,
  type Repartie,
} from "./monde";

/* ────────────────────────────────────────────────────────────────────────── */
/* Petites fabriques                                                          */
/* ────────────────────────────────────────────────────────────────────────── */

interface Reglages {
  creatrice: CreatriceDemo;
  fan: string;
  langue?: Langue;
  stage?: Stage;
  revenusCents?: number;
  nonLus?: number;
  tags?: string[];
  commandeEnCours?: boolean;
  /** Le montant de la commande en cours (cents) et depuis quand — la ligne de
   *  la pile les lit. Sans montant, la commande n'est qu'une pastille. */
  offreCents?: number;
  offreDepuisMin?: number;
  arriveIlYaMin?: number;
  /** Les flows qu'il a déjà reçus (« script 1 »…). */
  flowsRecus?: string[];
}

function fiche(r: Reglages): ApiConversationListItem {
  return {
    id: idDemo("conv"),
    creator_id: r.creatrice.id,
    creator_slug: r.creatrice.slug,
    creator_display_name: r.creatrice.nom,
    creator_avatar_url: r.creatrice.avatar_url,
    user_id: idDemo("user"),
    fan_name: r.fan,
    fan_avatar_url: null,
    telegram_chat_id: null,
    language: (r.langue ?? "fr").toUpperCase(),
    last_message: "",
    last_author: null,
    last_message_at: null,
    created_at: ilYA(r.arriveIlYaMin ?? 60 * 24 * 9),
    message_count: 0,
    revenue_cents: r.revenusCents ?? 0,
    has_active_order: r.commandeEnCours ?? false,
    offre_en_attente:
      r.commandeEnCours && r.offreCents
        ? { montant_cents: r.offreCents, devise: "EUR", depuis: ilYA(r.offreDepuisMin ?? 12) }
        : null,
    currency: "EUR",
    stage: r.stage ?? "fan_mid",
    bot_is_paused: false,
    // Cyrano n'est pas branché : il n'y a rien à mettre en pause, et aucune
    // carte ne doit porter l'état « Cyrano en pause » à l'image.
    is_cyrano_paused: false,
    unread_count: r.nonLus ?? 0,
    active_flow_run: null,
    tags: (r.tags ?? []).map(tagLite),
    invite_link_source: null,
    needs_attention: false,
    needs_attention_reason: null,
    live_signal: null,
    live_signal_energy: null,
    content_mode: "off",
    pending_decision: false,
    pending_decision_label: null,
    is_reachable: true,
    workflow_actif: null,
    signal_workflow: null,
  };
}

/**
 * Une ligne de dialogue :
 *   `lui`  — le fan (une phrase, ou un `Dit` quand il écrit une autre langue) ;
 *   `elle` — le chatteur, au nom de la créatrice. Écrite en FRANÇAIS : chez un
 *            fan étranger, elle part traduite (`traduireVers`), et la bulle
 *            garde l'original — exactement ce que montre le poste ;
 *   `flow` — une bulle d'un flow qu'on lui a lancé.
 * Le nombre est « il y a combien de minutes ».
 */
type Ligne = ["lui", string | Dit, number] | ["elle" | "flow", string, number];

/** Assemble une conversation complète, datée à rebours depuis la fin. */
function conversationDe(
  r: Reglages,
  lignes: Ligne[],
  extra?: {
    repliques?: Array<string | Dit>;
    script?: Repartie[];
    scriptApresLeFlow?: Repartie[];
    pendantLeFlow?: Dit[];
    apresLeFlow?: Dit[];
    /** Décore le Nième message (index dans `lignes`). */
    decors?: Record<number, Partial<Parameters<typeof message>[0]>>;
  },
): ConversationDemo {
  const f = fiche(r);
  const langue = r.langue ?? "fr";
  const conv: ConversationDemo = {
    fiche: f,
    fan: profilFan(r.fan),
    messages: [],
    note: noteVide(),
    tags: f.tags ?? [],
    repliques: extra?.repliques ?? [],
    brouillons: [],
    langue,
    flowsRecus: r.flowsRecus ?? [],
    ...(extra?.script ? { script: extra.script.map((t) => ({ ...t })) } : {}),
    ...(extra?.scriptApresLeFlow
      ? { scriptApresLeFlow: extra.scriptApresLeFlow.map((t) => ({ ...t })) }
      : {}),
    ...(extra?.pendantLeFlow ? { pendantLeFlow: [...extra.pendantLeFlow] } : {}),
    ...(extra?.apresLeFlow ? { apresLeFlow: [...extra.apresLeFlow] } : {}),
    scene: false,
  };

  conv.messages = lignes.map(([qui, texte, minutes], i) => {
    const created_at = ilYA(minutes);
    const decor = extra?.decors?.[i] ?? {};
    if (qui === "lui") return { ...bulleDuFan(conv, texte, created_at), ...decor };
    const fr = texte as string;
    const traduit = langue === "fr" ? null : traduireVers(fr, langue);
    return message({
      direction: "out",
      created_at,
      content: traduit ?? fr,
      sender_source: qui === "flow" ? "flow" : "operator",
      ...(traduit
        ? { traduction: { source: fr, depuis: "fr", vers: langue, provenance: "llm" } }
        : {}),
      ...decor,
    });
  });

  const dernier = conv.messages[conv.messages.length - 1];
  f.message_count = conv.messages.length;
  if (dernier) {
    f.last_message = dernier.content ?? "";
    f.last_message_traductions = dernier.traductions ?? null;
    f.last_author = dernier.direction === "in" ? "fan" : "bot";
    f.last_message_at = dernier.created_at;
  }
  return conv;
}

/**
 * Le début du flow « script 1 », tel qu'un ancien fan l'a reçu — mot pour mot
 * la prod (`regie-test/candidate/vente/scenario.ts`, `SCRIPT_1`). Un fan qui
 * est là depuis des jours a reçu son flow d'accueil : son fil doit le porter,
 * sinon on montre un poste où personne n'a jamais lancé de flow.
 */
function debutDuScript1(ilYaMin: number): Ligne[] {
  return [
    ["flow", "je devrais pas te dire ça", ilYaMin],
    ["flow", "mais t'es arrivé au mauvais moment, ou au bon", ilYaMin],
    ["lui", "mdr pourquoi ?", ilYaMin - 1],
    ["flow", "attends", ilYaMin - 1],
    ["flow", "avant que je te raconte, t'es tranquille là ou pas", ilYaMin - 2],
  ];
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Le peuplement de fond                                                      */
/* ────────────────────────────────────────────────────────────────────────── */

const STAGES: Stage[] = [
  "fan_low", "fan_mid", "customer_low", "fan_high",
  "customer_mid", "fan_mid", "customer_high", "gfe",
];

/** Le français domine — c'est la réalité du répertoire — et les autres
 *  langues passent assez souvent pour qu'un filtre ait de quoi trier. */
const ORDRE_DES_LANGUES: Langue[] = [
  "fr", "fr", "en", "fr", "de", "fr", "fr", "it", "fr", "es", "fr", "en", "fr", "pt",
];

function choisir<T>(liste: readonly T[], n: number): T {
  return liste[n % liste.length]!;
}

/**
 * Les conversations d'avant le shift : des fans qui ont reçu leur flow, à qui
 * un chatteur a parlé, qui ont parfois payé. La plupart attendent LE FAN —
 * c'est l'état ordinaire d'un répertoire ; une sur quatre a un fan qui a
 * écrit en dernier et attend qu'on lui réponde.
 */
function peuplement(creatrices: CreatriceDemo[], combien: number): ConversationDemo[] {
  const out: ConversationDemo[] = [];
  const tours = new Map<Langue, number>();

  for (let i = 0; i < combien; i++) {
    const langue = choisir(ORDRE_DES_LANGUES, i);
    const pool = FANS[langue];
    const n = tours.get(langue) ?? 0;
    tours.set(langue, n + 1);

    const stage = choisir(STAGES, i);
    const paye = stage.startsWith("customer") || stage === "gfe";
    // Il y a 1 à 20 jours ; le fil s'étale sur quelques heures ce jour-là.
    const debut = 60 * 24 * (1 + (i % 20)) + 7 * i;
    const lignes: Ligne[] = [
      ...choisir(pool.arrivees, n).map((d, k): Ligne => ["lui", d, debut + 3 - k]),
      ...(langue === "fr" ? debutDuScript1(debut) : []),
      ["lui", choisir(pool.repliques, n), debut - 30],
      ["elle", "dis moi ce que t'aimerais voir, je vais te chauffer comme il faut 😏", debut - 32],
    ];
    if (paye) {
      lignes.push(["lui", choisir(pool.prix, n), debut - 50]);
      lignes.push([
        "elle",
        "j'ai une vidéo de 6 min où je me touche en pensant à un mec comme toi… rien que pour toi c'est 50 😏",
        debut - 52,
      ]);
      lignes.push(["elle", "merci bb 😘 regarde la jusqu'au bout et dis moi ce que t'en penses", debut - 70]);
    }
    // Une sur quatre : le fan est revenu, et personne ne lui a encore répondu.
    const attend = i % 4 === 1;
    if (attend) lignes.push(["lui", choisir(pool.retours, n), 20 + 9 * i]);

    out.push(
      conversationDe(
        {
          creatrice: choisir(creatrices, i),
          fan: choisir(pool.prenoms, n),
          langue,
          stage,
          revenusCents: paye ? 5000 * (1 + (i % 3)) : 0,
          nonLus: attend ? 1 : 0,
          arriveIlYaMin: debut + 5,
          tags: i % 9 === 0 && paye ? ["VIP"] : [],
          flowsRecus: langue === "fr" ? ["script 1"] : [],
        },
        lignes,
        { repliques: [...pool.repliques] },
      ),
    );
  }
  return out;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Les scènes                                                                 */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Ce que le fan répond au fil de « script 1 » — les répliques du test de
 * candidature, écrites pour ce flow : courtes, style texto, dans l'ordre où le
 * flow attend une réponse.
 */
const REPONSES_AU_SCRIPT_1: Dit[] = [
  "mdr pourquoi ?",
  "oui tranquille",
  "vas-y chauffe moi 😏",
  "je t'embrasserais partout",
  "je te plaque contre le mur et je t'embrasse dans le cou",
].map((t) => ({ texte: t, fr: t }));

const fr = (t: string): Dit => ({ texte: t, fr: t });

export function construireLesConversations(
  creatrices: CreatriceDemo[],
): ConversationDemo[] {
  if (creatrices.length === 0) return [];
  const une = creatrices[0]!;
  const deux = creatrices[1] ?? une;
  const trois = creatrices[2] ?? une;

  const scenes: ConversationDemo[] = [];

  /* ── 1. LE NOUVEAU — la scène principale de la formation ────────────── */
  /* Il vient d'arriver de Twitter. Le geste : l'ajouter à ma file, lancer
     « script 1 », laisser le flow écrire ; quand le flow se termine sur
     « tu vas devoir me payer », il demande combien. Francis dit alors de
     proposer LA vidéo, à 50 ; le chatteur envoie le lien ; il paie. */
  scenes.push(
    conversationDe(
      { creatrice: une, fan: "Lucas", stage: "new", nonLus: 2, arriveIlYaMin: 4 },
      [
        ["lui", "salut", 4],
        ["lui", "je viens de ton twitter 😊", 3],
      ],
      {
        // Avant le flow : s'il reçoit un mot, il répond, sans brûler la scène.
        repliques: [fr("mdr t'es rapide"), fr("ça va et toi ?")],
        pendantLeFlow: REPONSES_AU_SCRIPT_1,
        apresLeFlow: [fr("ok je continue"), fr("c'est combien ?")],
        scriptApresLeFlow: [
          // Elle propose la vidéo, faite pour lui, à 50.
          {
            sur: "texte",
            dit: ["putain ouais", "envoie moi ça"],
            relance: ["et donc c'est combien ?", "tu me proposes quoi ?"],
          },
          // Le lien — et il paie (`paiementDuFan`).
          {
            sur: "transaction",
            dit: ["c'est payé 💸"],
            relance: ["tu m'envoies le lien ?", "bah envoie le lien alors"],
          },
          {
            sur: "texte",
            dit: ["j'ai hâte de voir ça 😍"],
            relance: ["t'es toujours là ?"],
          },
        ],
      },
    ),
  );

  /* ── 2. L'ANGLAIS — la traduction, dans les deux sens ───────────────── */
  /* Il a eu son accueil il y a trois jours (traduit : la bulle porte ce que
     le chatteur avait écrit en français), il revient et demande un prix. Ses
     messages arrivent traduits ; ce que Francis propose part en anglais. */
  scenes.push(
    conversationDe(
      {
        creatrice: deux,
        fan: "Connor",
        langue: "en",
        stage: "fan_high",
        nonLus: 2,
        arriveIlYaMin: 60 * 24 * 3,
      },
      [
        ["lui", { texte: "hey", fr: "salut" }, 60 * 24 * 3],
        ["elle", "hey toi 😊 bienvenue, t'as fait vite", 60 * 24 * 3 - 2],
        ["lui", { texte: "you're so hot", fr: "t'es trop sexy" }, 60 * 24 * 3 - 5],
        ["elle", "dis moi ce que t'aimerais voir, je vais te chauffer comme il faut 😏", 60 * 24 * 3 - 6],
        ["lui", { texte: "hey you there?", fr: "hé t'es là ?" }, 3],
        ["lui", { texte: "how much for a video?", fr: "c'est combien pour une vidéo ?" }, 2],
      ],
      {
        script: [
          {
            sur: "texte",
            dit: [
              { texte: "ok that sounds hot", fr: "ok ça a l'air chaud" },
              { texte: "send me the link", fr: "envoie moi le lien" },
            ],
            relance: [{ texte: "so how much?", fr: "alors c'est combien ?" }],
          },
          {
            sur: "transaction",
            dit: [{ texte: "paid 💸", fr: "payé 💸" }],
            relance: [{ texte: "where's the link?", fr: "il est où le lien ?" }],
          },
          {
            sur: "texte",
            dit: [{ texte: "can't wait 😍", fr: "j'ai hâte 😍" }],
          },
        ],
        repliques: [...FANS.en.repliques],
      },
    ),
  );

  /* ── 3. Il dit qu'il a payé — seul Dropp le dira, rien ne se confirme ── */
  scenes.push(
    conversationDe(
      {
        creatrice: deux,
        fan: "Dorian",
        stage: "customer_high",
        revenusCents: 24000,
        nonLus: 1,
        commandeEnCours: true,
        offreCents: 7000,
        offreDepuisMin: 12,
        tags: ["PIN", "Gros payeur"],
        arriveIlYaMin: 60 * 24 * 55,
        flowsRecus: ["script 1", "script 2"],
      },
      [
        ["lui", "t'as encore un truc comme la dernière fois ?", 40],
        ["elle", "j'ai encore plus fou que ça, tu veux que je te montre ? 😏", 38],
        ["lui", "vas y", 30],
        ["elle", "https://dropp.fans/demo", 12],
        ["lui", "c'est parti, j'ai envoyé", 3],
      ],
      {
        repliques: ["c'est validé de mon côté", "tu me l'envoies quand ?"],
      },
    ),
  );

  /* ── 4. « c'est cher » — un prix ne redescend pas ───────────────────── */
  scenes.push(
    conversationDe(
      {
        creatrice: une,
        fan: "Kevin",
        stage: "fan_high",
        nonLus: 2,
        arriveIlYaMin: 60 * 24 * 4,
        flowsRecus: ["script 1"],
      },
      [
        ...debutDuScript1(60 * 6),
        ["lui", "c'est combien ?", 50],
        ["elle", "j'ai une vidéo de 6 min où je me touche en pensant à un mec comme toi… rien que pour toi c'est 50 😏", 45],
        ["lui", "50 balles c'est chaud quand même", 8],
        ["lui", "t'as pas moins cher ?", 7],
      ],
      {
        script: [
          { sur: "texte", dit: ["bon ok à 40 je prends"], relance: ["allez fais un effort"] },
          { sur: "transaction", dit: ["payé"], relance: ["envoie le lien"] },
        ],
        repliques: ["merci 😘"],
      },
    ),
  );

  /* ── 5. Celui qui veut la voir en vrai — le réel se détourne ────────── */
  scenes.push(
    conversationDe(
      {
        creatrice: deux,
        fan: "Mehdi",
        stage: "customer_mid",
        revenusCents: 10000,
        nonLus: 2,
        tags: ["IRL"],
        arriveIlYaMin: 60 * 24 * 30,
        flowsRecus: ["script 1"],
      },
      [
        ["elle", "merci bb 😘 regarde la jusqu'au bout et dis moi ce que t'en penses", 60 * 24 * 2],
        ["lui", "franchement t'es incroyable", 60 * 24 * 2 - 10],
        ["lui", "on se voit quand en vrai ?", 15],
        ["lui", "je peux monter samedi", 14],
      ],
      { repliques: ["ok montre moi alors 😏", "je veux voir ça"] },
    ),
  );

  /* ── 6. Celui qui veut tout gratuit ─────────────────────────────────── */
  scenes.push(
    conversationDe(
      {
        creatrice: trois,
        fan: "Ilyes",
        stage: "fan_low",
        nonLus: 1,
        arriveIlYaMin: 60 * 24 * 2,
        flowsRecus: ["script 1"],
      },
      [
        ...debutDuScript1(60 * 20),
        ["lui", "envoie une photo gratuite pour voir", 25],
      ],
      { repliques: ["bon ok c'est combien"] },
    ),
  );

  /* ── 7. L'italien qui vient d'arriver ───────────────────────────────── */
  scenes.push(
    conversationDe(
      { creatrice: trois, fan: "Luca", langue: "it", stage: "new", nonLus: 2, arriveIlYaMin: 11 },
      [
        ["lui", { texte: "ciao", fr: "salut" }, 11],
        ["lui", { texte: "ti ho trovata su twitter", fr: "je t'ai trouvée sur twitter" }, 10],
      ],
      { repliques: [...FANS.it.repliques] },
    ),
  );

  /* ── 8. Il a payé hier, il se tait — la deuxième marche ─────────────── */
  scenes.push(
    conversationDe(
      {
        creatrice: une,
        fan: "Marco",
        stage: "customer_low",
        revenusCents: 5000,
        arriveIlYaMin: 60 * 24 * 5,
        flowsRecus: ["script 1"],
      },
      [
        ["lui", "c'est combien ?", 60 * 26],
        ["elle", "j'ai une vidéo de 6 min où je me touche en pensant à un mec comme toi… rien que pour toi c'est 50 😏", 60 * 26 - 2],
        ["lui", "c'est payé", 60 * 26 - 10],
        ["elle", "merci bb 😘 regarde la jusqu'au bout et dis moi ce que t'en penses", 60 * 26 - 11],
      ],
      { repliques: ["t'es folle 😍", "elle était trop bien"] },
    ),
  );

  /* ── 9. Celle qu'on garde sous la main ──────────────────────────────── */
  scenes.push(
    conversationDe(
      {
        creatrice: une,
        fan: "Bastien",
        stage: "gfe",
        revenusCents: 61000,
        tags: ["PIN", "VIP"],
        arriveIlYaMin: 60 * 24 * 210,
        flowsRecus: ["script 1", "script 2"],
      },
      [
        ["lui", "bonne nuit ma belle", 400],
        ["elle", "t'es passé où ? j'ai pensé à toi aujourd'hui 😘", 398],
      ],
      { repliques: ["tu me manques"] },
    ),
  );

  /* ── 10. Celui qui parle depuis six mois et n'a jamais payé ─────────── */
  scenes.push(
    conversationDe(
      {
        creatrice: deux,
        fan: "Sami",
        stage: "fan_low",
        nonLus: 3,
        tags: ["Time Waster"],
        arriveIlYaMin: 60 * 24 * 180,
        flowsRecus: ["script 1", "script 2"],
      },
      [
        ["lui", "tu m'envoies un truc ?", 30],
        ["lui", "allez", 20],
        ["lui", "steuplé", 10],
      ],
      { repliques: ["j'ai pas d'argent", "je paierai jamais pour ça"] },
    ),
  );

  // ⛔ Le drapeau se pose ICI, sur ce qui vient d'être construit — la vie de
  //    fond n'écrit jamais dans une scène qu'on est en train de filmer.
  for (const c of scenes) c.scene = true;

  return [...scenes, ...peuplement(creatrices, 26)];
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Les arrivées pendant le shift                                              */
/* ────────────────────────────────────────────────────────────────────────── */

let arrivees = 0;

/**
 * Un fan qui ARRIVE maintenant — « des discussions qui démarrent de zéro »
 * (Kayla, 07-10-2026). Un premier message, parfois deux, rien d'autre : pas
 * de flow, pas de chatteur. C'est à quelqu'un de le prendre.
 */
export function unNouveauFan(creatrices: CreatriceDemo[]): {
  conv: ConversationDemo;
  bulles: Dit[];
} {
  const i = arrivees++;
  const langue = choisir(ORDRE_DES_LANGUES, i + 3);
  const pool = FANS[langue];
  const prenom = choisir(pool.prenoms, i + 5);
  const conv = conversationDe(
    {
      creatrice: choisir(creatrices, i),
      fan: prenom,
      langue,
      stage: "new",
      arriveIlYaMin: 0,
    },
    [],
    {
      repliques: [...pool.repliques],
      pendantLeFlow: langue === "fr" ? REPONSES_AU_SCRIPT_1 : undefined,
      apresLeFlow: [choisir(pool.prix, i)],
    },
  );
  return { conv, bulles: choisir(pool.arrivees, i) };
}

/** Repart de zéro — un monde neuf rejoue les mêmes arrivées. */
export function oublierLesArrivees(): void {
  arrivees = 0;
}
